// src/pages/layout/BusinessPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useParams, Link, useLocation } from "react-router-dom"; //useLocation
import { sectionTemplates, pageConfig } from "../../data/database";
import { pagesApi } from "../../api/pagesApi";
import FloatingWhatsAppButton from "../../components/FloatingWhatsAppButton";
import SectionWrapper from "../componenets/SectionWrapper";
import AccessibilityWidget from "../../components/AccessibilityWidget";
import InfoDialog from "../../components/GeneralComponents/InfoDialog"; 

import HeroSection1 from "../componenets/sections/herosections/HeroSection1";
import HeroSection2 from "../componenets/sections/herosections/HeroSection2";
import HeroSection3 from "../componenets/sections/herosections/HeroSection3";

import FeaturesSection1 from "../componenets/sections/featuressection/FeaturesSection1";
import FeaturesSection2 from "../componenets/sections/featuressection/FeaturesSection2";
import FeaturesSection3 from "../componenets/sections/featuressection/FeaturesSection3";

import TestimonialsSection1 from "../componenets/sections/testimonialssection/TestimonialsSection1";
import TestimonialsSection2 from "../componenets/sections/testimonialssection/TestimonialsSection2";
import TestimonialsSection3 from "../componenets/sections/testimonialssection/TestimonialsSection3";

import CTASection1 from "../componenets/sections/ctasection/CTASection1";
import CTASection2 from "../componenets/sections/ctasection/CTASection2";
import CTASection3 from "../componenets/sections/ctasection/CTASection3";

import GallerySection1 from "../componenets/sections/gallerysection/GallerySection1";
import GallerySection2 from "../componenets/sections/gallerysection/GallerySection2";
import GallerySection3 from "../componenets/sections/gallerysection/GallerySection3";

import CategorySection1 from "../componenets/sections/categorysection/CategorySection1";
import CategorySection2 from "../componenets/sections/categorysection/CategorySection2";
import CategorySection3 from "../componenets/sections/categorysection/CategorySection3";

import LocationSection1 from "../componenets/sections/locationsection/LocationSection1";
import LocationSection2 from "../componenets/sections/locationsection/LocationSection2";
import LocationSection3 from "../componenets/sections/locationsection/LocationSection3";

import ServiceSection1 from "../componenets/sections/servicesection/ServiceSection1";
import ServiceSection2 from "../componenets/sections/servicesection/ServiceSection2";
import ServiceSection3 from "../componenets/sections/servicesection/ServiceSection3";

import FooterSection1 from "../componenets/sections/footersection/FooterSection1";
import NavSection1 from "../componenets/sections/navsection/NavSection1";

const DEFAULT_GENERAL = {
  phone: "",
  city: "",
  logoUrl: "",
  pageBgColor: "#ffffff",
  scrollAnim: "fadeUp", // none | fadeUp | fade | slideLeft | slideRight | zoom
  scrollOnce: true,
  scrollBehavior: "smooth", // auto | smooth
};

const SCROLL_ANIM_VARIANTS = {
  fadeUp: {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
  },
  fade: {
    hidden: { opacity: 0 },
    show: { opacity: 1 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: 28 },
    show: { opacity: 1, x: 0 },
  },
  slideRight: {
    hidden: { opacity: 0, x: -28 },
    show: { opacity: 1, x: 0 },
  },
  zoom: {
    hidden: { opacity: 0, scale: 0.96 },
    show: { opacity: 1, scale: 1 },
  },
};

const resolveSectionTypeKey = (rawType, templatesObj) => {
  const t = (rawType || "").trim();
  if (!t) return t;

  const candidates = [
    t,
    t.toLowerCase(),

    // category
    t === "categorySection" ? "categorysection" : null,
    t === "categorysection" ? "categorySection" : null,

    // location
    t === "locationSection" ? "locationsection" : null,
    t === "locationsection" ? "locationSection" : null,

    // service
    t === "serviceSection" ? "servicesection" : null,
    t === "servicesection" ? "serviceSection" : null,
  ].filter(Boolean);

  return candidates.find((k) => templatesObj?.[k]) || t;
};

export default function BusinessPage({
  isPreview = false,
  previewConfig = null,
  onSectionClick = null,
  slugOverride = "",
}) {
  const params = useParams();
  const routeSlug = params.slug;

  // ✅ NEW: read dialog payload after manager redirect
  const location = useLocation();
  const [dialog, setDialog] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  useEffect(() => {
    const payload = location.state?.dialog;
    if (!payload) return;

    setDialog({ open: true, ...payload });

    // clear state so refresh doesn't show it again
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const effectiveSlug = (routeSlug || slugOverride || "").trim();
  const shouldFetchFromSlug = !!effectiveSlug && !previewConfig?.sections;

  const fallbackConfig = useMemo(() => {
    return {
      sections: pageConfig?.businessPage?.sections || [],
      footer: pageConfig?.businessPage?.footer || null,
      nav: pageConfig?.businessPage?.nav || null,
      whatsapp: pageConfig?.businessPage?.whatsapp || null,
      general: DEFAULT_GENERAL,
    };
  }, []);

  // Important: if a slug exists (public page), start with null to avoid flashing defaults.
  const [activeConfig, setActiveConfig] = useState(shouldFetchFromSlug ? null : fallbackConfig);
  const [loading, setLoading] = useState(shouldFetchFromSlug);
  const [loadedSlug, setLoadedSlug] = useState(shouldFetchFromSlug ? "" : effectiveSlug || "");
  const [notFound, setNotFound] = useState(false);

  // ✅ Fix horizontal scrolling: lock overflow on html/body while this page is mounted
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflowX = html.style.overflowX;
    const prevBodyOverflowX = body.style.overflowX;

    html.style.overflowX = "hidden";
    body.style.overflowX = "hidden";

    return () => {
      html.style.overflowX = prevHtmlOverflowX;
      body.style.overflowX = prevBodyOverflowX;
    };
  }, []);

  // ✅ Optional: dev-only detector to find what is causing overflow (prints in console)
  useEffect(() => {
    const isDev = typeof process !== "undefined" && process.env?.NODE_ENV !== "production";
    if (!isDev) return;

    // run after layout settles
    const t = setTimeout(() => {
      try {
        const bad = [];
        const nodes = document.querySelectorAll("body *");
        nodes.forEach((el) => {
          if (!el) return;
          if (el.scrollWidth > el.clientWidth + 2) {
            bad.push({
              el,
              scrollWidth: el.scrollWidth,
              clientWidth: el.clientWidth,
              className: el.className,
            });
          }
        });

        if (bad.length) {
          // eslint-disable-next-line no-console
          console.group("Horizontal overflow candidates");
          // eslint-disable-next-line no-console
          console.table(
            bad.slice(0, 15).map((x) => ({
              scrollWidth: x.scrollWidth,
              clientWidth: x.clientWidth,
              className: x.className,
              tag: x.el?.tagName,
            }))
          );
          // eslint-disable-next-line no-console
          console.log("Tip: look for w-screen, 100vw, negative margins, or wide flex rows.");
          // eslint-disable-next-line no-console
          console.groupEnd();
        }
      } catch {
        // ignore
      }
    }, 50);

    return () => clearTimeout(t);
  }, [effectiveSlug, activeConfig]);

  useEffect(() => {
    let ignore = false;

    async function run() {
      if (previewConfig?.sections) {
        setLoading(false);
        setNotFound(false);
        setLoadedSlug(effectiveSlug || "__preview__");
        setActiveConfig({
          sections: previewConfig.sections || [],
          footer: previewConfig.footer || null,
          nav: previewConfig.nav || null,
          whatsapp: previewConfig.whatsapp || null,
          general: { ...DEFAULT_GENERAL, ...(previewConfig.general || {}) },
        });
        return;
      }

      if (!effectiveSlug) {
        setLoading(false);
        setNotFound(false);
        setLoadedSlug("__fallback__");
        setActiveConfig(fallbackConfig);
        return;
      }

      setLoading(true);
      setNotFound(false);

      try {
        const page = await pagesApi.getBySlug(effectiveSlug);
        if (ignore) return;

        setLoadedSlug(effectiveSlug);
        setActiveConfig({
          sections: page.sections || [],
          footer: page.footer || null,
          nav: page.nav || null,
          whatsapp: page.whatsapp || null,
          general: { ...DEFAULT_GENERAL, ...(page.general || {}) },
        });
        setLoading(false);
      } catch (e) {
        if (ignore) return;

        setLoadedSlug(effectiveSlug);
        setNotFound(true);
        setActiveConfig(null);
        setLoading(false);
      }
    }

    run();
    return () => {
      ignore = true;
    };
  }, [effectiveSlug, previewConfig, fallbackConfig]);

  const reduceMotion = useReducedMotion();

  const scrollAnim =
    reduceMotion ? "none" : activeConfig?.general?.scrollAnim || DEFAULT_GENERAL.scrollAnim;

  const scrollOnce =
    typeof activeConfig?.general?.scrollOnce === "boolean"
      ? activeConfig.general.scrollOnce
      : DEFAULT_GENERAL.scrollOnce;

  const scrollBehavior = activeConfig?.general?.scrollBehavior || DEFAULT_GENERAL.scrollBehavior;
  const pageBgColor = activeConfig?.general?.pageBgColor || DEFAULT_GENERAL.pageBgColor;

  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = scrollBehavior === "smooth" ? "smooth" : "auto";
    return () => {
      html.style.scrollBehavior = prev;
    };
  }, [scrollBehavior]);

  const getMergedSectionData = useCallback((section) => {
    const typeKey = resolveSectionTypeKey(section?.type, sectionTemplates);
    const templateData = sectionTemplates?.[typeKey]?.[section?.template] || null;

    const mergedConfig = {
      ...(templateData?.config || {}),
      ...(section?.config || {}),
    };

    const mergedStyle = {
      ...(templateData?.style || {}),
      ...(section?.style || {}),
    };

    return { typeKey, templateData, mergedConfig, mergedStyle };
  }, []);

  const mergedNav = useMemo(() => {
    const navKey = "navsection";
    const defaultTpl = sectionTemplates?.[navKey]?.template1 || null;

    const saved = activeConfig?.nav || null;

    const base = saved || {
      enabled: true,
      template: "template1",
      config: {},
      style: {},
    };

    const tpl = sectionTemplates?.[navKey]?.[base.template || "template1"] || defaultTpl;
    if (!tpl) return null;

    return {
      enabled: base.enabled !== false,
      config: { ...(tpl?.config || {}), ...(base.config || {}) },
      style: { ...(tpl?.style || {}), ...(base.style || {}) },
      template: base.template || "template1",
    };
  }, [activeConfig?.nav]);

  const mergedFooter = useMemo(() => {
    const footerKey = "footersection";
    const defaultTpl = sectionTemplates?.[footerKey]?.template1 || null;

    const saved = activeConfig?.footer || null;

    const base = saved || {
      enabled: true,
      template: "template1",
      config: {},
      style: {},
    };

    const tpl = sectionTemplates?.[footerKey]?.[base.template || "template1"] || defaultTpl;
    if (!tpl) return null;

    return {
      enabled: base.enabled !== false,
      config: { ...(tpl?.config || {}), ...(base.config || {}) },
      style: { ...(tpl?.style || {}), ...(base.style || {}) },
      template: base.template || "template1",
    };
  }, [activeConfig?.footer]);

const renderSection = useCallback(
  (section) => {
    if (!section?.enabled) return null;

    const { typeKey, templateData, mergedConfig, mergedStyle } = getMergedSectionData(section);
    if (!templateData) return null;

    const businessPhone = activeConfig?.general?.phone || "";

    const sectionProps = {
      key: section.id || `${typeKey}-${section.template}`,
      config: mergedConfig,
      style: mergedStyle,
    };

    switch (`${typeKey}-${section.template}`) {
      case "hero-template1":
        return <HeroSection1 {...sectionProps} />;
      case "hero-template2":
        return <HeroSection2 {...sectionProps} />;
      case "hero-template3":
        return <HeroSection3 {...sectionProps} />;

      case "features-template1":
        return <FeaturesSection1 {...sectionProps} />;
      case "features-template2":
        return <FeaturesSection2 {...sectionProps} />;
      case "features-template3":
        return <FeaturesSection3 {...sectionProps} />;

      case "testimonials-template1":
        return <TestimonialsSection1 {...sectionProps} />;
      case "testimonials-template2":
        return <TestimonialsSection2 {...sectionProps} />;
      case "testimonials-template3":
        return <TestimonialsSection3 {...sectionProps} />;

      case "cta-template1":
        return <CTASection1 {...sectionProps} businessPhone={businessPhone} />;
      case "cta-template2":
        return <CTASection2 {...sectionProps} businessPhone={businessPhone} />;
      case "cta-template3":
        return <CTASection3 {...sectionProps} businessPhone={businessPhone} />;

      case "gallery-template1":
        return <GallerySection1 {...sectionProps} />;
      case "gallery-template2":
        return <GallerySection2 {...sectionProps} />;
      case "gallery-template3":
        return <GallerySection3 {...sectionProps} />;

      case "categorysection-template1":
      case "categorySection-template1":
        return <CategorySection1 {...sectionProps} />;
      case "categorysection-template2":
      case "categorySection-template2":
        return <CategorySection2 {...sectionProps} />;
      case "categorysection-template3":
      case "categorySection-template3":
        return <CategorySection3 {...sectionProps} />;

      case "locationsection-template1":
      case "locationSection-template1":
        return <LocationSection1 {...sectionProps} />;
      case "locationsection-template2":
      case "locationSection-template2":
        return <LocationSection2 {...sectionProps} />;
      case "locationsection-template3":
      case "locationSection-template3":
        return <LocationSection3 {...sectionProps} />;

      case "servicesection-template1":
      case "serviceSection-template1":
        return <ServiceSection1 {...sectionProps} />;
      case "servicesection-template2":
      case "serviceSection-template2":
        return <ServiceSection2 {...sectionProps} />;
      case "servicesection-template3":
      case "serviceSection-template3":
        return <ServiceSection3 {...sectionProps} />;

      default:
        return (
          <div className="py-20 px-4 text-center">
            <div className="text-gray-400">
              Template {typeKey}-{section.template} not found
            </div>
          </div>
        );
    }
  },
  [getMergedSectionData, activeConfig?.general?.phone]
);

  const showLoader =
    (shouldFetchFromSlug && (loading || !activeConfig || loadedSlug !== effectiveSlug)) &&
    !previewConfig?.sections;

  if (showLoader) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: pageBgColor }}
    >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin" />
          <div className="text-gray-600">Loading page...</div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: pageBgColor }}
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 text-center">
          <div className="text-3xl mb-3">📄</div>
          <div className="text-xl font-bold text-gray-900">Page not found</div>
          <div className="text-gray-600 mt-2">
            This slug does not exist yet. Create it from the admin.
          </div>
          <div className="mt-5 flex gap-3 justify-center">
            <Link
              to="/admin"
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Go to Admin
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full max-w-full overflow-x-hidden"
      style={{ backgroundColor: pageBgColor }}
    >
      {/*show dialog if manager was redirected with reason */}
      <InfoDialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onClose={() => setDialog((p) => ({ ...p, open: false }))}
      />

  {mergedNav?.enabled ? (
    <NavSection1
      config={mergedNav.config}
      style={mergedNav.style}
      general={activeConfig?.general}
      pageSlug={effectiveSlug}
    />
  ) : null}

      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        {(activeConfig?.sections || []).map((section, index) => {
          const { mergedStyle } = getMergedSectionData(section);

          const key = section.id || index;
          const variants = SCROLL_ANIM_VARIANTS[scrollAnim];
          const shouldAnimate = !!variants && section?.enabled;

          const content = (
            <SectionWrapper
              id={section.id || section.type || `section-${index}`}
              type={section.type}
              style={mergedStyle}
              isPreview={isPreview}
              onClick={onSectionClick ? () => onSectionClick(section) : null}
              bgColor={mergedStyle?.bgMode === "solid" ? mergedStyle?.bgColor : undefined}
            >
              {renderSection(section)}
            </SectionWrapper>
          );

          if (!shouldAnimate) return <React.Fragment key={key}>{content}</React.Fragment>;

          return (
            <motion.div
              key={key}
              className="w-full max-w-full overflow-x-hidden"
              initial="hidden"
              whileInView="show"
              viewport={{ once: scrollOnce, amount: 0.2 }}
              variants={variants}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              {content}
            </motion.div>
          );
        })}

     {mergedFooter?.enabled ? (
      <FooterSection1
        config={mergedFooter.config}
        style={mergedFooter.style}
        general={activeConfig?.general}
        pageSlug={effectiveSlug}
      />
    ) : null}
      </div>

      <FloatingWhatsAppButton config={activeConfig?.whatsapp} />

      {!isPreview && (
        <AccessibilityWidget direction={activeConfig?.general?.direction || "ltr"} />
      )}
    </div>
  );
}